import { WarningTwoIcon } from '@chakra-ui/icons';
import {
    Card,
    CardBody,
    Flex,
    HStack,
    IconButton,
    Image,
    Spinner,
    Tooltip
} from '@chakra-ui/react';
import { RefreshIcon, TrashIcon } from '../icons';
import Switch from '../switch';
import Text from '../text';

interface InfoCardProps {
    balance: number;
    logo: string;
    lastUpdate: string;
    isRefreshDisabled?: boolean;
    onDelete: () => void;
    onRefresh: () => void;
    refreshInProgress?: boolean;
    isToolTipDisabled?: boolean;
    isChecked?: boolean;
    onSelect: (isSelected: boolean) => void;
    errorMsg: string | null;
    colorMode: string | undefined;
    username: string;
    bookname: string;
    supportedExtensions: any[];
    extensionAuthToken: string |  null;
    bookRegionName?: string;
}

const formatCurrency = (balance: number): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(balance / 100);
};

function InfoCard(props: InfoCardProps) {
    const {
        balance,
        logo,
        lastUpdate,
        onDelete,
        onRefresh,
        onSelect,
        isChecked,
        isRefreshDisabled,
        isToolTipDisabled,
        refreshInProgress,
        errorMsg,
        colorMode,
        extensionAuthToken,
        username,
        bookname,
        supportedExtensions,
        bookRegionName
    } = props;

    var toolTipText = `Use the ${username} mobile app to refresh ${bookname}`;
    if (isRefreshDisabled && extensionAuthToken && supportedExtensions.length > 0) {
      toolTipText = `Download the SharpSports Chrome Extension to refresh ${bookname}`;
    }

    return (
        <Card
            shadow="none"
            border="1px"
            borderColor={colorMode == 'light' ? 'border' : 'transparent'}
            borderRadius={10}
            bg={colorMode == 'light' ? 'white' : 'alphaWhite005'}
        >
            <CardBody p="20px">
                <Flex
                    direction="column"
                    gap="10px"
                    color={colorMode == 'light' ? 'navy' : 'white'}
                >
                    <Flex align="center" justify="space-between">
                        <Flex align="flex-end" gap="8px" flex="1">
                            <Image w="35%" flexShrink={0} src={logo} alt="" />
                            {bookRegionName && (
                                <Text as="span" size="sm" fontSize="14px" color={colorMode == 'light' ? 'gray.600' : 'gray.400'} whiteSpace="nowrap" pb="2px">
                                    {bookRegionName}
                                </Text>
                            )}
                        </Flex>
                        <Switch
                            isChecked={isChecked}
                            isDisabled={refreshInProgress}
                            onChange={(e) => onSelect(e.currentTarget.checked)}
                        />
                    </Flex>
                    <Flex direction="column" align="flex-start" gap="9px">
                        <Text as="span" size="sm">
                            Balance
                        </Text>
                        <Text as="strong" size="lg" lineHeight="16px">
                            {formatCurrency(balance)}
                        </Text>
                    </Flex>
                    <Flex align="center" justify="space-between" gap="9px">
                        <Flex align="center" gap="9px">
                            <Text as="span" size="sm">
                                Last Update :
                            </Text>
                            <Text as="strong" size="sm">
                                {lastUpdate}
                            </Text>
                        </Flex>
                        {refreshInProgress ? (
                            <Flex align="center" gap="10px">
                                <Spinner />
                            </Flex>
                        ) : (
                            <Flex align="center" gap="10px">
                                <Tooltip label={toolTipText} placement='right-end' isDisabled={isToolTipDisabled}>
                                    <IconButton
                                        onClick={onRefresh}
                                        aria-label="Refresh"
                                        color={
                                            colorMode == 'light'
                                                ? 'midnightBlue'
                                                : 'skyBlue'
                                        }
                                        icon={<RefreshIcon />}
                                        variant="icon"
                                        isDisabled={isRefreshDisabled}
                                    />
                                </Tooltip>
                                <IconButton
                                    onClick={onDelete}
                                    aria-label="TrashIcon"
                                    color="red"
                                    icon={<TrashIcon />}
                                    variant="icon"
                                />
                            </Flex>

                        )}
                    </Flex>
                    {errorMsg && (
                        <HStack gap={'9px'}>
                            <WarningTwoIcon
                                h={'32px'}
                                w={'32px'}
                                color={'red'}
                            />
                            <Text size="s" lineHeight="16px" align="left">
                                {errorMsg}
                            </Text>
                        </HStack>
                    )}
                </Flex>
            </CardBody>
        </Card>
    );
}

export default InfoCard;
